/**
 * 列表 Table 個人偏好（columnOrder／columnSizing）本機暫存。
 * 形狀對齊未來 PUT /me/table-preferences；本模組不打 HTTP。
 */

export const TABLE_PREFS_SCHEMA_VERSION = 1;

const STORAGE_PREFIX = 'chenyo.userPrefs.v1.table';

/**
 * @param {string} userId
 * @param {string} tableKey
 * @returns {string|null}
 */
export function buildTablePrefsStorageKey(userId, tableKey) {
  if (!userId || !tableKey) {
    return null;
  }
  return `${STORAGE_PREFIX}.${userId}.${tableKey}`;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} raw
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   columnOrder: string[],
 *   columnSizing: Record<string, number>,
 * }|null}
 */
export function normalizeTablePrefsBlob(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }

  const schemaVersion = Number(raw.schemaVersion);
  if (schemaVersion !== TABLE_PREFS_SCHEMA_VERSION) {
    return null;
  }

  const columnOrder = Array.isArray(raw.columnOrder)
    ? raw.columnOrder.filter((id) => typeof id === 'string' && id.length > 0)
    : [];
  const columnSizing = isPlainObject(raw.columnSizing)
    ? Object.fromEntries(
      Object.entries(raw.columnSizing).filter(([, size]) => Number.isFinite(Number(size))),
    )
    : {};

  return {
    schemaVersion: TABLE_PREFS_SCHEMA_VERSION,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
    columnOrder,
    columnSizing,
  };
}

/**
 * @param {string} userId
 * @param {string} tableKey
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   columnOrder: string[],
 *   columnSizing: Record<string, number>,
 * }|null}
 */
export function readTablePrefs(userId, tableKey) {
  const key = buildTablePrefsStorageKey(userId, tableKey);
  if (!key || typeof localStorage === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return normalizeTablePrefsBlob(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * @param {string} userId
 * @param {string} tableKey
 * @param {{
 *   columnOrder?: string[],
 *   columnSizing?: Record<string, number>,
 * }} patch
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   columnOrder: string[],
 *   columnSizing: Record<string, number>,
 * }|null}
 */
export function writeTablePrefs(userId, tableKey, patch = {}) {
  const key = buildTablePrefsStorageKey(userId, tableKey);
  if (!key || typeof localStorage === 'undefined') {
    return null;
  }

  const prev = readTablePrefs(userId, tableKey) ?? {
    schemaVersion: TABLE_PREFS_SCHEMA_VERSION,
    updatedAt: '',
    columnOrder: [],
    columnSizing: {},
  };

  const next = {
    schemaVersion: TABLE_PREFS_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    columnOrder: Array.isArray(patch.columnOrder)
      ? patch.columnOrder.filter((id) => typeof id === 'string' && id.length > 0)
      : prev.columnOrder,
    columnSizing: isPlainObject(patch.columnSizing)
      ? Object.fromEntries(
        Object.entries(patch.columnSizing).filter(([, size]) => Number.isFinite(Number(size))),
      )
      : prev.columnSizing,
  };

  try {
    localStorage.setItem(key, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}
